"use client";

import type { JSX, ReactNode } from "react";
import { useMediaQuery } from "usehooks-ts";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselNextWithProgress,
  CarouselPrevious,
} from "@repo/ui/carousel";
import { cn } from "@repo/ui/utils";

// Mirrors HeaderVisual so carousel slides match the static image header.
const sizeClasses = {
  large: "h-[596px] desktop-sm:h-[760px]",
  small: "h-[280px] desktop-sm:h-[650px]",
} as const;

const PREV_CLASSES = "absolute left-4 top-1/2 z-[2] -translate-y-1/2";
const NEXT_CLASSES = "absolute right-4 top-1/2 z-[2] -translate-y-1/2";

export interface HeaderCarouselSlide {
  id: string;
  headline?: string;
  backgroundSlot: ReactNode;
  ctaSlot?: ReactNode;
}

export interface HeaderCarouselProps {
  slides: HeaderCarouselSlide[];
  /** Seconds a slide stays on screen. Autoplay is driven by the next button's progress ring and disabled entirely for reduced-motion users. */
  autoplayDelaySeconds?: number;
  size?: keyof typeof sizeClasses;
  /** Accessible name for the carousel region — pass a localized value from the block layer. */
  ariaLabel?: string;
  /** Accessible name for the previous-slide button — pass a localized value from the block layer. */
  prevLabel?: string;
  /** Accessible name for the next-slide button — pass a localized value from the block layer. */
  nextLabel?: string;
  className?: string;
}

/**
 * Ring-driven autoplay like the other autoplaying carousels (district-map,
 * image-feature, general-banner): the progress ring on the next button IS the
 * autoplay timer — no Embla autoplay plugin. From `tablet` up the arrows sit
 * at the sides (original position); below that they move to the bottom right
 * of the card at small size, on the content band's baseline (bottom-16 = the
 * slide's pb-16). Both sets render and CSS toggles them — the small ring is
 * `noAutoplayAdvance` so only the large instance advances (WAAPI keeps running
 * on display:none, same trick as CarouselNavButtons). Reduced-motion users get
 * plain arrows and no autoplay.
 */
function HeaderCarouselControls({
  autoplayDelayMs,
  prevLabel,
  nextLabel,
}: {
  /** null disables autoplay (reduced motion). */
  autoplayDelayMs: number | null;
  prevLabel: string;
  nextLabel: string;
}): JSX.Element {
  return (
    <>
      {/* Mobile: bottom right of the card */}
      <div className="pointer-events-none absolute inset-x-0 bottom-16 z-[2] tablet:hidden">
        <div className="container mx-auto flex justify-end">
          <div className="pointer-events-auto flex items-center gap-2">
            <CarouselPrevious size="sm" aria-label={prevLabel} />
            {autoplayDelayMs === null ? (
              <CarouselNext size="sm" aria-label={nextLabel} />
            ) : (
              <CarouselNextWithProgress
                size="sm"
                autoplayDelay={autoplayDelayMs}
                noAutoplayAdvance
                wrapperClassName="size-8"
                aria-label={nextLabel}
              />
            )}
          </div>
        </div>
      </div>

      {/* Tablet up: side arrows, vertically centered (original position) */}
      <CarouselPrevious
        className={cn(PREV_CLASSES, "hidden tablet:inline-flex")}
        aria-label={prevLabel}
      />
      {autoplayDelayMs === null ? (
        <CarouselNext
          className={cn(NEXT_CLASSES, "hidden tablet:inline-flex")}
          aria-label={nextLabel}
        />
      ) : (
        <CarouselNextWithProgress
          autoplayDelay={autoplayDelayMs}
          wrapperClassName={cn(NEXT_CLASSES, "hidden tablet:inline-flex")}
          aria-label={nextLabel}
        />
      )}
    </>
  );
}

export function HeaderCarousel({
  slides,
  autoplayDelaySeconds = 5,
  size = "large",
  ariaLabel = "Header highlights",
  prevLabel = "Previous slide",
  nextLabel = "Next slide",
  className,
}: HeaderCarouselProps): JSX.Element | null {
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
    { initializeWithValue: false },
  );

  if (slides.length === 0) {
    return null;
  }

  return (
    <Carousel
      loop
      duration={40}
      aria-label={ariaLabel}
      // bg: during live window drag-resize the compositor repositions the
      // translate3d track a frame behind layout, briefly exposing the page
      // background at the resizing edge. A dark underlay makes that strip
      // invisible against the photography (white reads as flicker).
      className={cn("relative w-full bg-deep-sea-blue-500", className)}
      data-testid="header-carousel"
    >
      <CarouselContent className="ml-0">
        {slides.map((slide) => (
          <CarouselItem key={slide.id} className="pl-0">
            {/* dark: slides sit on photography, so semantic tokens (buttons, text) use their on-dark values */}
            <article
              className={cn(
                "dark relative flex w-full flex-col items-start justify-end overflow-hidden pb-16 tablet:pb-24",
                sizeClasses[size],
              )}
            >
              {slide.backgroundSlot && (
                <div className="absolute inset-0 z-0">
                  {slide.backgroundSlot}
                </div>
              )}

              <div className="absolute inset-0 z-[1] bg-gradient-to-t from-black/70 from-5% via-black/25 via-45% to-transparent" />

              <div className="container relative z-[2] mx-auto flex w-full flex-col items-start gap-6">
                {slide.headline && (
                  <h2 className="typography-mobile-display-d2 desktop-sm:typography-desktop-display-d2 w-full min-w-0 max-w-[793px] text-white">
                    {slide.headline}
                  </h2>
                )}
                {slide.ctaSlot}
              </div>
            </article>
          </CarouselItem>
        ))}
      </CarouselContent>

      {slides.length > 1 && (
        <HeaderCarouselControls
          autoplayDelayMs={
            prefersReducedMotion ? null : autoplayDelaySeconds * 1000
          }
          prevLabel={prevLabel}
          nextLabel={nextLabel}
        />
      )}
    </Carousel>
  );
}
